import { useState } from "react";
import { useCart } from "@/lib/store";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["DC","District of Columbia"],
  ["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],
  ["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],
  ["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],
  ["PA","Pennsylvania"],["PR","Puerto Rico"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addr, setAddr] = useState({ address1: "", address2: "", city: "", state: "", postal: "", country: "US" });
  const [quoting, setQuoting] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const addrReady = addr.address1 && addr.city && addr.postal && addr.country;
  const flatShipping = subtotal >= 80 || subtotal === 0 ? 0 : 5.9;
  const shipping = selectedQuote ? selectedQuote.amount : flatShipping;
  const total = subtotal + shipping;

  const getQuotes = async () => {
    if (!addrReady) {
      toast.error("Completa la dirección primero");
      return;
    }
    setQuoting(true); setQuotes(null); setSelectedQuote(null);
    try {
      const itemCount = items.reduce((s, i) => s + i.qty, 0);
      const { data } = await api.post("/ups/rates", {
        destination: { name, phone, ...addr },
        item_count: itemCount,
      });
      setQuotes(data || []);
      if (data && data.length) setSelectedQuote(data[0]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 503) {
        toast.info("UPS no está activo — usaremos la tarifa estándar.");
      } else {
        toast.error(detail?.message || detail || "No se pudieron obtener tarifas UPS");
      }
      setQuotes([]);
    } finally {
      setQuoting(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, size_ml: i.size_ml, qty: i.qty })),
        origin_url,
        email,
        customer_name: name,
        shipping_address: addrReady ? { name, phone, ...addr } : null,
        ups_service_code: selectedQuote?.service_code || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar el pago");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-4xl">Tu bolsa está vacía</h1>
        <p className="text-muted-foreground mt-4">Descubre nuestra selección y añade fragancias antes de finalizar la compra.</p>
        <Link to="/shop" className="inline-block mt-8 bg-navy text-cream px-8 py-4 font-mono-caps hover:bg-cream hover:text-navy transition-colors">
          Explorar boutique
        </Link>
      </div>
    );
  }

  const setField = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });
  const inp = "w-full border border-border p-4 focus:border-navy outline-none";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="checkout-page">
      <div className="mb-10">
        <div className="font-mono-caps text-muted-foreground">Checkout</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-2">Finalizar compra</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
        <form onSubmit={pay} className="space-y-10" data-testid="checkout-form">
          {/* 1 · Contact */}
          <div>
            <div className="font-mono-caps text-xs mb-4">1 · Contacto</div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="checkout-email" placeholder="tu@email.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground mb-2">Nombre completo</label>
                  <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className={inp} data-testid="checkout-name" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-2">Teléfono</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inp} data-testid="checkout-phone" />
                </div>
              </div>
            </div>
          </div>

          {/* 2 · Address */}
          <div>
            <div className="font-mono-caps text-xs mb-2 flex items-center justify-between">
              <span>2 · Dirección de envío</span>
              <span className="text-[10px] text-royal">Envíos solo a EE.UU.</span>
            </div>
            <div className="space-y-4">
              <AddressAutocomplete
                value={addr.address1}
                onChange={(v) => setAddr((a) => ({ ...a, address1: v }))}
                onSelect={(s) => setAddr((a) => ({
                  ...a,
                  address1: s.address1 || a.address1,
                  city: s.city || a.city,
                  state: s.state || a.state,
                  postal: s.postal || a.postal,
                  country: "US",
                }))}
                placeholder="Street address"
              />
              <input placeholder="Apt, suite, etc. (optional)" value={addr.address2} onChange={setField("address2")} className={inp} data-testid="checkout-address2" />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="City" value={addr.city} onChange={setField("city")} className={inp} data-testid="checkout-city" />
                <select required value={addr.state} onChange={setField("state")} className={inp} data-testid="checkout-state">
                  <option value="">State</option>
                  {US_STATES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="ZIP code" value={addr.postal} onChange={setField("postal")} className={inp} data-testid="checkout-postal" />
                <div className={`${inp} flex items-center text-sm text-muted-foreground bg-muted`} data-testid="checkout-country">
                  🇺🇸 United States
                </div>
              </div>
            </div>
          </div>

          {/* 3 · Shipping method */}
          <div>
            <div className="font-mono-caps text-xs mb-4 flex items-center justify-between">
              <span>3 · Método de envío</span>
              <button
                type="button"
                onClick={getQuotes}
                disabled={!addrReady || quoting}
                className="text-navy underline text-xs disabled:opacity-40 hover:text-royal"
                data-testid="checkout-get-quotes"
              >
                {quoting ? "Calculando UPS…" : "Calcular tarifas UPS"}
              </button>
            </div>
            {quoting && (
              <div className="border border-border p-6 flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 strokeWidth={1.25} className="h-4 w-4 animate-spin" /> Consultando UPS…
              </div>
            )}
            {!quoting && quotes && quotes.length > 0 && (
              <div className="space-y-2" data-testid="checkout-quotes">
                {quotes.map((q) => {
                  const active = selectedQuote?.service_code === q.service_code;
                  return (
                    <button
                      type="button"
                      key={q.service_code}
                      onClick={() => setSelectedQuote(q)}
                      className={`w-full flex items-center justify-between p-4 border transition-colors ${active ? "border-navy bg-navy text-cream" : "border-border hover:border-navy"}`}
                      data-testid={`quote-${q.service_code}`}
                    >
                      <div className="text-left">
                        <div className="font-mono-caps text-xs">{q.service_name}</div>
                        <div className={`text-xs mt-1 ${active ? "text-cream/70" : "text-muted-foreground"}`}>UPS · {q.currency}</div>
                      </div>
                      <div className="font-serif text-lg">${Number(q.amount).toFixed(2)}</div>
                    </button>
                  );
                })}
              </div>
            )}
            {!quoting && quotes && quotes.length === 0 && (
              <div className="border border-border p-6 text-sm text-muted-foreground">
                Sin tarifas UPS disponibles. Se aplicará la tarifa estándar (${flatShipping.toFixed(2)}).
              </div>
            )}
            {!quoting && !quotes && (
              <div className="border border-border p-6 flex items-start gap-4">
                <Truck strokeWidth={1.25} className="h-5 w-5 text-navy mt-1" />
                <div className="flex-1 text-sm">
                  <p className="font-serif text-base">Envío estándar</p>
                  <p className="text-muted-foreground mt-1">{flatShipping === 0 ? "Gratis a partir de $80" : `$${flatShipping.toFixed(2)} · 24-48h`}. Completa la dirección y pulsa <em>Calcular tarifas UPS</em> para ver opciones oficiales.</p>
                </div>
              </div>
            )}
          </div>

          {/* 4 · Payment */}
          <div>
            <div className="font-mono-caps text-xs mb-4">4 · Pago</div>
            <div className="border border-border p-6 flex items-start gap-4">
              <Lock strokeWidth={1.25} className="h-5 w-5 text-navy mt-1" />
              <div>
                <p className="font-serif text-lg">Pago seguro con Stripe</p>
                <p className="text-sm text-muted-foreground mt-1">Serás redirigido a la pasarela segura. Aceptamos tarjeta, Apple Pay y Google Pay.</p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="checkout-pay-btn"
            className="w-full bg-navy text-cream py-5 font-mono-caps hover:bg-cream hover:text-navy transition-colors disabled:opacity-50"
          >
            {loading ? "Redirigiendo…" : `Pagar $${total.toFixed(2)}`}
          </button>

          <div className="flex flex-col sm:flex-row items-start gap-4 pt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><ShieldCheck strokeWidth={1.25} className="h-4 w-4 text-navy" /> Perfumes 100% originales</div>
            <div className="flex items-center gap-2"><Truck strokeWidth={1.25} className="h-4 w-4 text-navy" /> Envío 24-48h</div>
          </div>
        </form>

        {/* Summary */}
        <aside className="bg-muted p-8 lg:p-10 h-fit" data-testid="checkout-summary">
          <div className="font-mono-caps text-xs mb-6">Tu pedido</div>
          <ul className="divide-y">
            {items.map((it) => (
              <li key={`${it.product_id}-${it.size_ml}`} className="py-4 flex gap-4">
                <img src={it.image} alt={it.name} className="w-16 h-20 object-cover bg-white" />
                <div className="flex-1">
                  <div className="font-mono-caps text-xs text-muted-foreground">{it.brand}</div>
                  <div className="font-serif">{it.name}</div>
                  <div className="text-xs text-muted-foreground">{it.size_ml}ml · {it.qty}</div>
                </div>
                <div className="text-sm">${(it.price * it.qty).toFixed(2)}</div>
              </li>
            ))}
          </ul>
          <div className="border-t mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-muted-foreground">
              <span>Envío {selectedQuote ? `· ${selectedQuote.service_name}` : ""}</span>
              <span>{shipping === 0 ? "Gratis" : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-serif text-2xl border-t pt-3 mt-3"><span>Total</span><span>${total.toFixed(2)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
